import React from 'react';
import { useTranslation } from 'react-i18next';
import { KRAJ } from '../kraj';

// Treść sekcji „Parametry elektryczne" na karcie modelu (bez własnej karty
// i bez nagłówka — te trzyma ProductPanels).
// Plan: PLAN_PARAMETRY_ELEKTRYCZNE.md §3.
//
// Źródłem jest WYŁĄCZNIE `electrical_spec` z API, czyli dane katalogowe producenta
// dowiązane po kodzie modelu. Backend nie wystawia tu estymat i front nie liczy
// tu niczego samodzielnie — brak pola znaczy brak wiersza, brak obiektu znaczy
// brak całej sekcji (żadnego pustego przycisku „Parametry elektryczne").
//
// Jeden komponent obsługuje panele, falowniki i magazyny; różni je tylko
// konfiguracja grup w `GRUPY` (klucze jak w URL: panel / inwerter / magazyn).
//
// `title` i `label` to klucze słownika `model` (el.<klucz>), nie gotowe teksty.
// Tłumaczymy je dopiero przy renderze, więc kolejność importów nie ma znaczenia.

const nf = (decimals) => new Intl.NumberFormat(KRAJ.locale, {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals,
});

const num = (value, unit, decimals = 2) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return `${nf(decimals).format(n)}${unit ? ` ${unit}` : ''}`;
};

const int = (value, unit) => num(value, unit, 0);
const text = (value) => (value === null || value === undefined || String(value).trim() === '' ? null : String(value));

// Okno MPPT to jedna informacja zapisana w dwóch polach — pokazujemy ją jako
// zakres, bo osobno „min" i „max" nic projektantowi nie mówią.
const zakres = (from, to, unit, decimals = 0) => {
  const a = num(from, null, decimals);
  const b = num(to, null, decimals);
  if (!a || !b) return null;
  return `${a}–${b} ${unit}`;
};

const GRUPY = {
  panel: [
    {
      title: 'stc',
      rows: [
        { label: 'pmax', get: (v) => num(v.pmax_w, 'Wp', 0) },
        { label: 'vmp', get: (v) => num(v.vmp_v, 'V') },
        { label: 'imp', get: (v) => num(v.imp_a, 'A') },
        { label: 'voc', get: (v) => num(v.voc_v, 'V') },
        { label: 'isc', get: (v) => num(v.isc_a, 'A') },
      ],
    },
    {
      title: 'wspolczynniki',
      rows: [
        { label: 'gamma_pmax', get: (v) => num(v.gamma_pmax, '%/°C', 3) },
        { label: 'alpha_isc', get: (v) => num(v.alpha_isc, '%/°C', 3) },
        { label: 'beta_voc', get: (v) => num(v.beta_voc, '%/°C', 3) },
      ],
    },
    {
      title: 'budowa',
      rows: [
        { label: 'liczba_ogniw', get: (v) => int(v.cells_series) },
        { label: 'technologia', get: (v) => text(v.technology) },
        { label: 'noct', get: (v) => num(v.noct_c, '°C', 1) },
      ],
    },
  ],
  inwerter: [
    {
      title: 'strona_ac',
      rows: [
        { label: 'moc_znamionowa', get: (v) => num(v.ac_power_kw, 'kW') },
        { label: 'moc_maksymalna', get: (v) => num(v.ac_power_max_kw, 'kW') },
        { label: 'prad_maksymalny', get: (v) => num(v.ac_current_max_a, 'A') },
        { label: 'liczba_faz', get: (v) => text(v.phase) },
      ],
    },
    {
      title: 'strona_dc',
      rows: [
        { label: 'maks_napiecie_dc', get: (v) => num(v.dc_max_voltage_v, 'V', 0) },
        { label: 'okno_mppt', get: (v) => zakres(v.mppt_v_min, v.mppt_v_max, 'V') },
        { label: 'liczba_mppt', get: (v) => int(v.mppt_count) },
        { label: 'wejsc_na_mppt', get: (v) => int(v.dc_inputs_per_mppt) },
        { label: 'maks_prad_mppt', get: (v) => num(v.mppt_current_max_a, 'A') },
        { label: 'maks_isc_mppt', get: (v) => num(v.mppt_isc_max_a, 'A') },
      ],
    },
    {
      title: 'sprawnosc',
      rows: [
        { label: 'maksymalna', get: (v) => num(v.eff_max, '%', 1) },
        { label: 'europejska', get: (v) => num(v.eff_euro, '%', 1) },
      ],
    },
  ],
  // Magazyny: karty niskonapięciowe opisują zdolność PRĄDEM, wysokonapięciowe
  // MOCĄ — dlatego oba komplety pól są w jednej grupie, a puste wiersze i tak
  // odpadają. Kolejność grup od tego, co decyduje o zgodności z falownikiem.
  magazyn: [
    {
      title: 'pojemnosc_energia',
      rows: [
        { label: 'pojemnosc_nominalna', get: (v) => num(v.capacity_kwh, 'kWh') },
        { label: 'energia_uzytkowa', get: (v) => num(v.usable_energy_kwh, 'kWh') },
        { label: 'pojemnosc_ogniw', get: (v) => num(v.capacity_ah, 'Ah', 0) },
        { label: 'dod', get: (v) => num(v.dod_percent, '%', 0) },
        { label: 'chemia', get: (v) => text(v.chemistry) },
      ],
    },
    {
      title: 'napiecie',
      rows: [
        { label: 'napiecie_nominalne', get: (v) => num(v.voltage_nominal_v, 'V', 1) },
        // Jedno miejsce po przecinku, inaczej niż przy oknie MPPT falownika.
        // Tam zakres liczy się w setkach woltów i ułamek nic nie wnosi, tu
        // 44,8–57,6 V zaokrąglone do 45–58 V gubi informację, która realnie
        // decyduje o zgodności z wejściem bateryjnym falownika.
        { label: 'okno_pracy', get: (v) => zakres(v.voltage_min_v, v.voltage_max_v, 'V', 1) },
      ],
    },
    {
      title: 'ladowanie_rozladowanie',
      rows: [
        { label: 'maks_prad_ladowania', get: (v) => num(v.charge_current_max_a, 'A', 0) },
        { label: 'maks_prad_rozladowania', get: (v) => num(v.discharge_current_max_a, 'A', 0) },
        { label: 'moc_ladowania', get: (v) => num(v.charge_power_kw, 'kW') },
        { label: 'moc_rozladowania', get: (v) => num(v.discharge_power_kw, 'kW') },
        { label: 'moc_szczytowa', get: (v) => num(v.peak_power_kw, 'kW') },
      ],
    },
    {
      title: 'trwalosc',
      rows: [
        { label: 'liczba_cykli', get: (v) => int(v.cycle_life) },
        { label: 'przepustowosc', get: (v) => num(v.energy_throughput_mwh, 'MWh', 0) },
      ],
    },
    {
      title: 'warunki_pracy',
      rows: [
        {
          label: 'temp_ladowania',
          get: (v) => zakres(v.temp_charge_min_c, v.temp_charge_max_c, '°C'),
          // Większość magazynów nie ładuje się poniżej 0 °C, więc w polskiej
          // zimie w nieogrzewanym garażu po prostu przestaje przyjmować energię.
          // Model, który to potrafi, ma realną przewagę — i warto ją pokazać.
          wyroznienie: (v) => (Number(v.temp_charge_min_c) < 0 ? 'laduje_ponizej_zera' : null),
        },
        { label: 'temp_rozladowania', get: (v) => zakres(v.temp_discharge_min_c, v.temp_discharge_max_c, '°C') },
        { label: 'stopien_ochrony', get: (v) => text(v.ip_rating) },
        { label: 'maks_rownolegle', get: (v) => int(v.max_parallel_units) },
      ],
    },
  ],
};

const formatujDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(KRAJ.locale);
};

// Grupy z wypełnionymi wartościami — puste wiersze i puste grupy odpadają tutaj,
// żeby liczenie parametrów i renderowanie zawsze widziały to samo.
const widoczneGrupy = (spec, category) => {
  const grupy = GRUPY[category];
  if (!spec || !grupy) return [];
  const values = spec.values || {};
  return grupy
    .map((g) => ({
      title: g.title,
      rows: g.rows
        .map((r) => ({
          label: r.label,
          value: r.get(values),
          wyroznienie: r.wyroznienie ? r.wyroznienie(values) : null,
        }))
        .filter((r) => r.value !== null),
    }))
    .filter((g) => g.rows.length > 0);
};

// Liczba parametrów do etykiety zakładki. Zwraca 0, gdy nie ma czego pokazać —
// wołający decyduje wtedy, czy w ogóle rysować sekcję.
export const policzParametry = (spec, category) =>
  widoczneGrupy(spec, category).reduce((sum, g) => sum + g.rows.length, 0);

// SAMA TREŚĆ, bez karty i bez nagłówka: zwijanie i pasek zakładek trzyma
// ProductPanels, żeby „otwarta może być tylko jedna sekcja" było jedną decyzją
// w jednym miejscu, a nie umową między dwoma niezależnymi komponentami.
// Etykieta źródła przychodzi z backendu po polsku (SPEC_SOURCE_LABEL_PL w models.py).
// Znane etykiety mają tłumaczenie w el.zrodla, nieznana zostaje bez zmian.
const ZRODLA = {
  'weryfikacja własna VoltValue': 'wlasna',
  'karta katalogowa producenta': 'karta',
  'karta katalogowa producenta (.OND)': 'ond',
  'California Energy Commission Solar Equipment Lists': 'cec',
  'baza komponentów PVsyst': 'pvsyst',
  'zestawienie danych z kart producentów': 'pvindex',
};

const ElectricalSpecBody = ({ spec, category }) => {
  const { t } = useTranslation('model');
  const widoczne = widoczneGrupy(spec, category);
  if (!widoczne.length) return null;

  const dataListingu = formatujDate(spec.listed_at);
  const zrodlo = ZRODLA[spec.source_label_pl]
    ? t(`el.zrodla.${ZRODLA[spec.source_label_pl]}`)
    : spec.source_label_pl;
  // Faza przychodzi jako wartość katalogu („3 Fazowy”); tłumaczymy tylko wyświetlany tekst.
  const wartosc = (r) => (r.label === 'liczba_faz' && r.value
    ? t(`wartosc.faza.${r.value}`, { defaultValue: r.value })
    : r.value);

  return (
    <>
      {/* Desktop: dwie kolumny grup. Mobile (360 px): jedna kolumna, etykieta
          i wartość w jednym wierszu. */}
      <div className="grid gap-x-10 gap-y-6 md:grid-cols-2">
        {widoczne.map((g) => (
          <section key={g.title} className="min-w-0">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-600">{t(`el.${g.title}`)}</h3>
            <dl className="mt-2 divide-y divide-gray-100">
              {g.rows.map((r) => (
                <div key={r.label} className="flex items-baseline justify-between gap-3 py-1.5">
                  <dt className="text-sm text-gray-500 min-w-0">{t(`el.${r.label}`)}</dt>
                  <dd className="text-sm font-semibold text-gray-800 text-right shrink-0">
                    <span className="whitespace-nowrap">{wartosc(r)}</span>
                    {r.wyroznienie ? (
                      <span className="ml-2 inline-block rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700 align-middle">
                        {t(`el.${r.wyroznienie}`)}
                      </span>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>

      <p className="mt-5 pt-3 border-t border-gray-100 text-xs text-gray-400">
        {t('el.zrodlo', { zrodlo })}
        {spec.model_code ? t('el.pozycja_katalogowa', { kod: spec.model_code }) : ''}
        {dataListingu ? t('el.wpis_z', { data: dataListingu }) : ''}
      </p>
    </>
  );
};

export default ElectricalSpecBody;
